/**
 * URL: /search/:content?q=foo
 *
 * /search?q=foo
 * /search/videos?q=foo
 * /search/documents?q=foo
 *
 * @urlParams
 * :content indicates which tab is active in UI,
 * (if empty defaults to 'all' tab.)
 *
 * @queryParams
 * q: search query
 */
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useSWRImmutable from 'swr/immutable'

import search, { SEARCH_HITS_INDEX } from '../../../../libraries/search'
import { useI18N } from '../../../../libraries/i18n/hook'
import VideoGrid from '../../../components/content/VideoGrid'
import DocumentGrid from '../../../components/content/DocumentGrid'
import PageSection from '../../../components/layout/PageSection'
import NoResults from '../../../components/layout/NoResults'
import { OuterTabGroup } from '../../../components/form/tab-groups'
import ErrorMessage from '../../../components/errors/ErrorMessage'
import Loading from '../../../components/layout/Loading'
import EventsList from '../../../components/events/List'
import SmallCompanyGrid from '../../../components/companies/SmallGrid'
import CompaniesGrid from '../../../components/companies/Grid'
import { useSearch } from '../../../../libraries/search/hook'
import { CompanyRowCard, DocumentRowCard, EventRowCard, VideoRowCard } from '../../../components/layout/search/RowCardResult'
import { CompanySearchResult, DocumentSearchResult, EventSearchResult, SEARCH_RESULT_TYPE, type VideoSearchResult } from '../../../../libraries/search/types'

enum TAB {
  ALL = 'all',
  VIDEOS = 'videos',
  EVENTS = 'events',
  DOCUMENTS = 'documents',
  COMPANIES = 'companies',
}
const getTabIdForContentUrl = (urlContent?: TAB) => {
  if(urlContent === TAB.VIDEOS) return TAB.VIDEOS
  if(urlContent === TAB.DOCUMENTS) return TAB.DOCUMENTS
  if(urlContent === TAB.EVENTS) return TAB.EVENTS
  if(urlContent === TAB.COMPANIES) return TAB.COMPANIES
  return TAB.ALL
}

interface Props { basePath: 'search' | 'search-mobile' }
const SearchResults:React.FC<Props> = ({ basePath }) => {
  const { t } = useI18N()
  const navigate = useNavigate()

  // @urlParams
  const { content } = useParams<{ content: TAB }>()
  // @queryParams
  const { q } = useSearch()

  const activeTab = getTabIdForContentUrl(content)

  const { data: results, isLoading: isLoadingAll, error: errorAll } = useSWRImmutable(['search', 'all', q], () => search(q))

  const { data: videoResults, isLoading: isLoadingVideos, error: errorVideos }
    = useSWRImmutable(activeTab === TAB.VIDEOS ? ['search', 'videos', q] : null, () => search(q, SEARCH_HITS_INDEX.VIDEOS))
  const { data: docsResults, isLoading: isLoadingDocs, error: errorDocs }
    = useSWRImmutable(activeTab === TAB.DOCUMENTS ? ['search', 'documents', q] : null, () => search(q, SEARCH_HITS_INDEX.DOCUMENTS))
  const { data: companiesResults, isLoading: isLoadingCompanies, error: errorCompanies }
    = useSWRImmutable(activeTab === TAB.COMPANIES ? ['search', 'companies', q] : null, () => search(q, SEARCH_HITS_INDEX.COMPANIES))
  const { data: eventsResults, isLoading: isLoadingEvents, error: errorEvents }
    = useSWRImmutable(activeTab === TAB.EVENTS ? ['search', 'events', q] : null, () => search(q, SEARCH_HITS_INDEX.EVENTS))

  const selectTab = (tab: string) => {
    const newTab = tab as TAB
    if(newTab !== TAB.ALL) {
      navigate(`/${basePath}/${tab}?q=` + q, { replace: true })
    } else {
      navigate(`/${basePath}?q=` + q, { replace: true })
    }
  }

  const showEventsResults = results?.events?.length || 0 > 0
  const showVideosResults = results?.videos?.length || 0 > 0
  const showDocumentsResults = results?.documents?.length || 0 > 0
  const showCompaniesResults = results?.companies?.length || 0 > 0

  const noResults = !showEventsResults && !showVideosResults && !showDocumentsResults && !showCompaniesResults
  const tabs = [
    { id: TAB.ALL, title: t('common.all') },
    { id: TAB.VIDEOS, title: `${t('content.videos')} ${!showVideosResults ? '(0)' : ''}`, disabled: !showVideosResults },
    { id: TAB.DOCUMENTS, title: `${t('content.documents')} ${!showDocumentsResults ? '(0)' : ''}`, disabled: !showDocumentsResults },
    { id: TAB.COMPANIES, title: `${t('companies.title')} ${!showCompaniesResults ? '(0)' : ''}`, disabled: !showCompaniesResults },
    { id: TAB.EVENTS, title: `${t('events.title')} ${!showEventsResults ? '(0)' : ''}`, disabled: !showEventsResults },
  ]

  const isLoading = isLoadingAll || isLoadingVideos || isLoadingDocs || isLoadingCompanies || isLoadingEvents
  const error = errorAll || errorVideos || errorDocs || errorCompanies || errorEvents


  const renderAllDesktopResults = () => (
    <>
      <div className='sm:grid grid-cols-2 gap-14'>
        {showEventsResults && (
          <PageSection seeAllReplace title={t('events.title')} link={(results?.events?.length ?? 0) >= 2 ? { label: t('common.seeAll'), to: `/${basePath}/events?q=${q}` } : undefined}>
            <EventsList data={results?.events} isLoading={isLoading} error={error} />
          </PageSection>
        )}
        {showCompaniesResults && (
          <PageSection seeAllReplace title={t('companies.title')} link={(results?.companies?.length ?? 0) >= 3 ? { label: t('common.seeAll', { context: 'fem' }), to: `/${basePath}/companies?q=${q}`} : undefined}>
            <SmallCompanyGrid data={results?.companies} isLoading={isLoading} error={error} />
          </PageSection>
        )}
      </div>
      {showVideosResults && (
        <PageSection seeAllReplace title={t('content.videos')} link={(results?.videos?.length ?? 0) >= 5 ? { label: t('common.seeAll'), to: `/${basePath}/videos?q=${q}`} : undefined}>
          <VideoGrid data={results?.videos} isLoading={isLoading} error={null} />
        </PageSection>
      )}
      {showDocumentsResults && (
        <PageSection seeAllReplace title={t('content.documents')} link={(results?.documents?.length ?? 0) >= 5 ? { label: t('common.seeAll'), to: `/${basePath}/documents?q=${q}`} : undefined}>
          <DocumentGrid data={results?.documents} isLoading={isLoading} error={null} />
        </PageSection>
      )}
    </>
  )
  const renderAllMobileResults = () => {
    const mixedResults: Array<React.ReactNode> = []
    results?.all?.forEach((result) => {
      let video: VideoSearchResult
      let document: DocumentSearchResult
      let company: CompanySearchResult
      let event: EventSearchResult

      switch(result._type) {
        case SEARCH_RESULT_TYPE.VIDEO:
          video = result as VideoSearchResult
          mixedResults.push(<VideoRowCard key={video.title} id={video.id} imgSrc={video.thumbnailsUrls.mobileVertical} title={video.title} description={`${video.type.name} · ${video.authors.join(', ')}`} />)
          break
        case SEARCH_RESULT_TYPE.DOCUMENT:
          document = result as DocumentSearchResult
          mixedResults.push(<DocumentRowCard key={document.title} id={document.id} icon={document.type.icon} title={document.title} description={`${document.type.name} · ${document.authors.join(', ')}`} />)
          break
        case SEARCH_RESULT_TYPE.COMPANY:
          company = result as CompanySearchResult
          mixedResults.push(<CompanyRowCard key={company.name} id={company.id} imgSrc={company.logoSrc} tagLabel={company.industry.name} />)
          break
        case SEARCH_RESULT_TYPE.EVENT:
          event = result as EventSearchResult
          mixedResults.push(<EventRowCard key={event.title} id={event.id} startDate={event.startDate} endDate={event.endDate} title={event.title} description={`${event.streamingLink ? 'Online · ' : ''}  ${event.authors.join(', ')}`} />)
          break
      }
    })
    return <div className='grid grid-cols-1 gap-3'>{mixedResults}</div>
  }

  const renderAllResults = () => (
    <>
      <div className='hidden md:block'>{renderAllDesktopResults()}</div>
      <div className='block md:hidden'>
        {renderAllMobileResults()}
      </div>
    </>
  )

  return (
    <div>
      <OuterTabGroup tabs={tabs} active={activeTab} handleClick={selectTab} />
      {error && <ErrorMessage error={new Error(error)} />}
      {isLoading && <div className='mt-8'><Loading size='fill-container' /></div>}
      {noResults && !isLoading && <NoResults className='mt-8' />}
      {!isLoading && !noResults && activeTab === TAB.ALL ? renderAllResults() : null}
      {!isLoading && activeTab === TAB.VIDEOS && (
        <VideoGrid data={videoResults?.videos} isLoading={isLoading} error={null} />
      )}
      {!isLoading && activeTab === TAB.DOCUMENTS && (
        <DocumentGrid data={docsResults?.documents} isLoading={isLoading} error={null} />
      )}
      {!isLoading && activeTab === TAB.COMPANIES && (
        <CompaniesGrid data={companiesResults?.companies} isLoading={isLoading} error={error} />
      )}
      {!isLoading && activeTab === TAB.EVENTS && (
        <EventsList data={eventsResults?.events} isLoading={isLoading} error={error} />
      )}
    </div>
  )
}

export default SearchResults
